import { getAuth } from "firebase/auth";
import { useRouter } from "next/dist/client/router";
import { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { AnyObject } from "yup/lib/types";
import Agent from "../../entities/Agent";
import AgentShop from "../../entities/AgentShop";
import Shop from '../../entities/Shop';
import ShopAuthority from "../../entities/ShopAuthority";
import firebase from "../../firebase/Firebase";
import { useSubscriptions } from "../../hooks/billingHooks";
import { isShopify, isWeb } from "../../lib/shop";
import { getAgent } from "../../repositories/agentRepo";
import { getAgentShop } from "../../repositories/agentShopRepo";
import { getShopAuthority } from "../../repositories/shopAuthorityRepo";
import { getShop } from "../../repositories/shopRepo";
import Loading from "../organisms/others/Loading";
import Plan from "../organisms/settings/Plan";
import StripePlan from "../organisms/settings/StripePlan";


type AuthContextProps = {
  currentAgent: Agent | null | undefined;
  currentShop: Shop | null | undefined;
  currentAuthority: ShopAuthority | null | undefined;
  currentPlans: any[] | null | undefined;
  agentShop: AgentShop | null | undefined;
  setCurrentShop: Dispatch<SetStateAction<Shop>>
  setCurrentAuthority: Dispatch<SetStateAction<ShopAuthority>>
  setCurrentAgent: Dispatch<SetStateAction<Agent>>
  setCurrentPlans: Dispatch<SetStateAction<any[]>>
  setAgentShopCtx: Dispatch<SetStateAction<AgentShop>>
};

const AuthContext = createContext<AuthContextProps>({
  currentAgent: undefined,
  currentShop: undefined,
  currentAuthority: undefined,
  currentPlans: undefined,
  agentShop: undefined,
  setCurrentShop: () => {},
  setCurrentAuthority: () => {},
  setCurrentAgent: () => {},
  setCurrentPlans: () => {},
  setAgentShopCtx: () => {}
});

export const AuthProvider = ({ children }:{ children }) => {
  const auth = getAuth(firebase)
  const router = useRouter()
  const [currentAgent, setCurrentAgent] = useState<Agent | null | undefined>(undefined);
  const [currentShop, setCurrentShop] = useState<Shop | null | undefined>(undefined);
  const [currentAuthority, setCurrentAuthority] = useState<ShopAuthority | null | undefined>(undefined);
  const [currentPlans, setCurrentPlans] = useState<AnyObject[] | null | undefined>(undefined);
  const [agentShop, setAgentShopCtx] = useState<AgentShop | null | undefined>()
  const [loading, setLoading] = useState(false);
  const exceptRedirect = ['/auth/login', '/auth/verifyEmail', '/auth/sent', '/auth/sentRegisterPassword',
                          '/auth/register', '/auth', '/auth/reset-password',
                          '/policy','/auth/callback', '/error', '/_error',
                          '/auth/selectShop', '/settings/plan/success']
  const {plans, setPlans, planLoading} = useSubscriptions(currentAuthority)
  useEffect(() => {
    setLoading(true)
    auth.onAuthStateChanged((user) => {
      if (user && user.emailVerified) {
        getAgent(user.uid).then((agent) => {
          getAgentShop(user.uid).then((agentShop) => {
            setCurrentAgent(agent);
            setLoading(false);
            setAgentShopCtx(agentShop)
          })
        })
      } else if (user && !user.emailVerified) {
        router.push('/auth/verifyEmail')
      } else {
        setCurrentAgent(null);
        setLoading(false)
        if (!exceptRedirect.includes(router.pathname)) {
          localStorage.clear()
          router.push('/auth/login')
        }
      }
    });
  }, [auth]);

  useEffect(() => {
    if (!agentShop) return
    if (!agentShop.currentShopId) {
      router.push('/auth/selectShop');
      return
    }
    getShop(agentShop.currentShopId, currentAgent?.id).then((shop) => {
      setCurrentShop(shop)
    })
    getShopAuthority(agentShop.currentShopId, currentAgent?.id).then((authority) => {
      setCurrentAuthority(authority)
    })
  }, [agentShop])

  useEffect(() => {
    if (!plans || plans.filter((plan) => plan.isActive()).length == 0) return
    if (exceptRedirect.includes(router.pathname)) return
    const activePlans = plans.filter((plan) => plan.isActive()) as Array<any>
    setCurrentPlans(activePlans)
  }, [plans])

  if ((!plans || plans.filter((plan) => plan.isActive()).length == 0) && !planLoading && !exceptRedirect.includes(router.pathname)) {
    if (isShopify(currentAuthority)) {
      return (<AuthContext.Provider value={{
        currentAgent: currentAgent,
        currentShop: currentShop,
        currentAuthority: currentAuthority,
        currentPlans: currentPlans,
        agentShop: agentShop,
        setCurrentShop: setCurrentShop,
        setCurrentAuthority: setCurrentAuthority,
        setCurrentAgent: setCurrentAgent,
        setCurrentPlans: setCurrentPlans,
        setAgentShopCtx: setAgentShopCtx
      }}>
        <Plan />
      </AuthContext.Provider>)
    } else if (isWeb(currentAuthority)) {
      return <StripePlan />
    }
  }

  if((loading || planLoading) && !exceptRedirect.includes(router.pathname)) {
    return <Loading />
  }

  return (
    <AuthContext.Provider value={{
      currentAgent: currentAgent,
      currentShop: currentShop,
      currentAuthority: currentAuthority,
      currentPlans: currentPlans,
      agentShop: agentShop,
      setCurrentShop: setCurrentShop,
      setCurrentAuthority: setCurrentAuthority,
      setCurrentAgent: setCurrentAgent,
      setCurrentPlans: setCurrentPlans,
      setAgentShopCtx: setAgentShopCtx
    }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuthContext = () => useContext(AuthContext);
export default useAuthContext